import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';
import type { Product } from '@/types/catalog';
import { maxOrderQty } from '@/lib/preorder';
import { trackAddToCart } from '@/lib/metaPixel';

export interface CartItem {
  product: Product;
  quantity: number;
}

interface CartContextValue {
  items: CartItem[];
  addItem: (product: Product, quantity?: number) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  itemCount: number;
  subtotal: number;
}

const CartContext = createContext<CartContextValue | null>(null);

const STORAGE_KEY = 'noobs_cart';
const STORAGE_VERSION = 2;

function loadCart(): CartItem[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    // Handle both legacy (array) and versioned ({ v, items }) formats.
    if (Array.isArray(parsed)) return parsed.filter(isValidCartItem);
    if (parsed?.v === STORAGE_VERSION && Array.isArray(parsed.items)) {
      return parsed.items.filter(isValidCartItem);
    }
    return [];
  } catch {
    return [];
  }
}

function isValidCartItem(item: unknown): item is CartItem {
  if (!item || typeof item !== 'object') return false;
  const it = item as Partial<CartItem>;
  return (
    !!it.product &&
    typeof (it.product as { id?: string }).id === 'string' &&
    typeof it.quantity === 'number' &&
    it.quantity > 0
  );
}

export function CartProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<CartItem[]>(loadCart);

  useEffect(() => {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ v: STORAGE_VERSION, items }),
    );
  }, [items]);

  const addItem = useCallback((product: Product, quantity = 1) => {
    setItems((prev) => {
      const max = maxOrderQty(product);
      const existing = prev.find((i) => i.product.id === product.id);
      if (existing) {
        const newQty = Math.min(existing.quantity + quantity, max);
        return prev.map((i) => i.product.id === product.id ? { ...i, quantity: newQty } : i);
      }
      return [...prev, { product, quantity: Math.min(quantity, max) }];
    });
    trackAddToCart({
      id: product.id,
      quantity: Math.max(1, Math.min(quantity, maxOrderQty(product) || 1)),
      item_price: Number(product.price) || 0,
      name: product.title_es,
    });
    // Let the header cart panel slide open so the customer sees the update.
    window.dispatchEvent(new CustomEvent('cart:open'));
  }, []);

  const removeItem = useCallback((productId: string) => {
    setItems((prev) => prev.filter((i) => i.product.id !== productId));
  }, []);

  const updateQuantity = useCallback((productId: string, quantity: number) => {
    if (quantity <= 0) {
      setItems((prev) => prev.filter((i) => i.product.id !== productId));
      return;
    }
    setItems((prev) =>
      prev.map((i) =>
        i.product.id === productId ? { ...i, quantity: Math.min(quantity, maxOrderQty(i.product)) } : i
      )
    );
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const itemCount = items.reduce((sum, i) => sum + i.quantity, 0);
  const subtotal = items.reduce((sum, i) => sum + i.product.price * i.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQuantity, clearCart, itemCount, subtotal }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used within CartProvider');
  return ctx;
}
